import type {
    AppliedFilter,
    CheckboxFilterGroup,
    Dealership,
    FilterGroup,
    FilterItem,
    FilterItemCheckboxValue,
    FilterItemValue,
    FilterSummary,
    FilterSummaryGroups
} from '../types';
import { CustomFilterGroups, isCheckboxFilter } from '../types';
import { getQueryParamByFilterId } from './getQueryParamByFilterId';
import { initialDealershipsFilterGroup } from '../constants';

// Normalize filter values and dropdown options to lowercase for case-insensitive comparison
export const normalizeValue = (value: string): string => {
    return value.toLowerCase();
};

export const applyFilterSummary = (filterList: FilterGroup[], summary?: FilterSummary): FilterGroup[] => {
    if (!summary) {
        return filterList;
    }

    return filterList.map((filterGroup) => {
        const isFilterGroupApplied = countAppliedItems(filterGroup) > 0;
        return isFilterGroupApplied ? filterGroup : buildFilterGroup(filterGroup, summary);
    });
};

const getUniqueCommonOrgIds = (dealerships: Dealership[]): string[] => {
    const commonOrgIds = dealerships.map((dealership) => dealership.commonOrgId).filter(Boolean);

    return Array.from(new Set(commonOrgIds));
};

export const appendDealershipsFilter = (
    filterList: FilterGroup[],
    dealerships: Dealership[] | undefined,
    orgId: string
): FilterGroup[] => {
    if (!dealerships || dealerships.length === 0) {
        return filterList;
    }

    const newFilterList = [...filterList];

    let dealershipsGroup = newFilterList.find(
        (filterGroup) => filterGroup.id === CustomFilterGroups.DEALERSHIPS
    ) as CheckboxFilterGroup;

    if (!dealershipsGroup) {
        dealershipsGroup = { ...initialDealershipsFilterGroup };
        newFilterList.push(dealershipsGroup);
    }

    const dealershipIds = getUniqueCommonOrgIds(dealerships);
    dealershipsGroup.items = dealershipIds.map((commonOrgId) => {
        const checked = commonOrgId === orgId;
        return {
            label: commonOrgId,
            value: checked,
            queryParameter: getQueryParamByFilterId(CustomFilterGroups.DEALERSHIPS)
        } as FilterItem<FilterItemCheckboxValue>;
    });

    return newFilterList;
};

export const buildFilterGroup = (filterGroup: FilterGroup, summary: FilterSummary): FilterGroup => {
    const summaryItems = getSummaryItems(filterGroup, summary);

    return {
        ...filterGroup,
        items: summaryItems || filterGroup.items
    } as FilterGroup;
};

export const getSummaryItems = (filterGroup: FilterGroup, summary: FilterSummary): FilterItem[] | undefined => {
    const summaryOptions = summary && summary[filterGroup.id as keyof FilterSummary];

    if (summaryOptions && typeof summaryOptions === 'object') {
        const reversed = filterGroup.settings?.reverse || false;
        const summaryOptionsKeys = Object.keys(summaryOptions);

        if (reversed) {
            summaryOptionsKeys.reverse();
        }

        return summaryOptionsKeys.map((label) => {
            const normalizedLabel = normalizeValue(label);

            // Use case-insensitive comparison for finding existing items
            const existingItem = filterGroup.items.find((item) => {
                const itemNormalizedLabel = item.normalizedLabel || normalizeValue(item.label);
                return itemNormalizedLabel === normalizedLabel;
            });

            if (existingItem) {
                // Ensure the existing item has a normalizedLabel
                return {
                    ...existingItem,
                    normalizedLabel: existingItem.normalizedLabel || normalizeValue(existingItem.label)
                };
            }

            return {
                label,
                value: getInitialValue(filterGroup),
                queryParameter: getQueryParamByFilterId(filterGroup.id as FilterSummaryGroups),
                normalizedLabel // Store normalized label for future comparisons
            };
        });
    }

    return undefined;
};

export const getInitialValue = (filterGroup: FilterGroup): FilterItemValue => {
    return isCheckboxFilter(filterGroup) ? false : '';
};

export const getAppliedFilters = (filterList: FilterGroup[]): AppliedFilter[] => {
    return filterList.flatMap((filterGroup) => {
        const { items: _items, ...groupInfo } = filterGroup;
        let appliedItems = filterGroup.items.filter((item) => item.value);

        if (filterGroup.id === CustomFilterGroups.DEALERSHIPS && appliedItems.length === 0) {
            appliedItems = filterGroup.items;
        }

        return appliedItems.map((appliedFilterItem) => {
            return {
                ...groupInfo,
                ...appliedFilterItem
            } as AppliedFilter;
        });
    });
};

export const updateFilter = (optionToUpdate: AppliedFilter, newValue: FilterItemValue, filters: FilterGroup[]): FilterGroup[] => {
    return filters.map((filterGroup) => {
        const filterItems = filterGroup.items as FilterItem[];
        const updatedItems: FilterItem[] = filterItems.map((item) => {
            // Use normalizedLabel if available, otherwise normalize the label
            const itemNormalizedLabel = item.normalizedLabel || normalizeValue(item.label);
            const optionNormalizedLabel = optionToUpdate.normalizedLabel || normalizeValue(optionToUpdate.label);

            // Use case-insensitive comparison for matching labels
            const shouldUpdate = itemNormalizedLabel === optionNormalizedLabel && filterGroup.id === optionToUpdate.id;

            // If updating, ensure the item has a normalizedLabel for future comparisons
            return shouldUpdate
                ? {
                      ...item,
                      value: newValue,
                      normalizedLabel: itemNormalizedLabel
                  }
                : item;
        });

        return {
            ...filterGroup,
            items: updatedItems
        } as FilterGroup;
    });
};

export const resetFilters = (optionsToReset: AppliedFilter[], filters: FilterGroup[]): FilterGroup[] => {
    return filters.map((filterGroup) => {
        const filterItems = filterGroup.items as FilterItem[];
        const clearedItems: FilterItem[] = filterItems.map((item) => {
            // Use normalizedLabel if available, otherwise normalize the label
            const itemNormalizedLabel = item.normalizedLabel || normalizeValue(item.label);

            // Use case-insensitive comparison for matching labels
            const shouldReset = optionsToReset.some(({ id, label, normalizedLabel }) => {
                const optionNormalizedLabel = normalizedLabel || normalizeValue(label);
                return itemNormalizedLabel === optionNormalizedLabel && filterGroup.id === id;
            });

            // Preserve the normalizedLabel even when resetting the value
            return shouldReset
                ? {
                      ...item,
                      value: getInitialValue(filterGroup),
                      normalizedLabel: itemNormalizedLabel
                  }
                : item;
        });

        return {
            ...filterGroup,
            items: clearedItems
        } as FilterGroup;
    });
};

export const resetAllFilters = (filters: FilterGroup[]): FilterGroup[] => {
    return filters.map((filterGroup) => {
        const filterItems = filterGroup.items as FilterItem[];
        const initialValue = getInitialValue(filterGroup);
        const clearedItems: FilterItem[] = filterItems.map((item) => ({
            ...item,
            value: initialValue
        }));

        return {
            ...filterGroup,
            items: clearedItems
        } as FilterGroup;
    });
};

export const countAppliedItems = (filter: FilterGroup): number => {
    return filter.items.reduce((count, item) => {
        const appliedCount = item.value ? 1 : 0;

        return count + appliedCount;
    }, 0);
};

export const getFilterOptionLabel = (
    label: string,
    groupId: string,
    dealerships: Dealership[] | undefined,
    currentDealer: Dealership | undefined
): string => {
    if (groupId === CustomFilterGroups.DEALERSHIPS && dealerships) {
        const matchingDealers = dealerships.filter((dealer) => dealer.commonOrgId === label);
        const dealershipNames = matchingDealers.map((dealer) => dealer.name || dealer.dealerName);
        const dealershipName = Array.from(new Set(dealershipNames)).join(' / ');

        if (dealershipName) {
            return dealershipName;
        }

        if (currentDealer && label === currentDealer.commonOrgId) {
            return currentDealer.name || currentDealer.dealerName || 'Default';
        }

        return label;
    }
    return label;
};
